import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocale } from '../i18n';

/** A box over the page (decision 171): a title, what it holds, closed by its ✕, Escape or a click outside it. */
export function Dialog({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const { t } = useLocale();
  const titleId = useId();
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    box.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current();
      // Tab goes round inside the box, never to the page behind it
      if (e.key === 'Tab' && box.current) {
        const all = [...box.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])')];
        if (all.length === 0) return;
        const first = all[0]!;
        const last = all[all.length - 1]!;
        const at = document.activeElement;
        if (e.shiftKey && (at === first || at === box.current)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && at === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    // the page behind does not scroll while the box is open
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      before?.focus();
    };
  }, []);
  // a press and its release both outside the box close it; a press on the backdrop's scrollbar (past its content
  // width) or a drag that began inside the box does not
  const downOutside = useRef(false);
  // at the top of the page, over the sidebar too: nothing on the page it opened from can clip or move it
  return createPortal(
    <div
      className="dialog-backdrop no-print"
      onMouseDown={(e) => {
        downOutside.current = e.target === e.currentTarget && e.clientX < (e.currentTarget.clientWidth || Infinity);
      }}
      onClick={(e) => {
        if (downOutside.current && e.target === e.currentTarget) onClose();
        downOutside.current = false;
      }}
    >
      <div ref={box} className={wide ? 'dialog dialog-wide' : 'dialog'} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <header className="dialog-head">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="icon-btn" aria-label={t('close')} onClick={onClose}>
            ✕
          </button>
        </header>
        <div className="dialog-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
